import type {
  CanonicalExperienceCapability,
  CanonicalExperienceContext,
} from "./canonicalExperienceContext";
import {
  resolveExperienceContext,
  type ExperienceContextInput,
} from "./resolveExperienceContext";
import {
  isHelperViewerRole,
  resolveActiveViewerRole,
} from "./relationshipsMapping";
import type {
  CyclePhase,
  CycleType,
  ExperienceContext,
  JourneyStage,
  OutcomeState,
  PartnerChoice,
  PrimaryIntent,
  SexAtBirth,
  TreatmentGoal,
  TreatmentModality,
  ViewerRole,
} from "./experience.types";

export type CanonicalExperienceContextFallback = {
  sexAtBirth?: SexAtBirth;
  partnerChoice?: PartnerChoice;
  /**
   * How the cycle this projection describes ended, as the client already reads
   * it off the cycle record.
   *
   * The projection reports the active cycle's type, status and phase but not its
   * outcome, and carries no closed cycle at all, so without this every
   * `results_*` surface is unreachable whenever the projection is available —
   * including a recorded result on a cycle that is still open, since recording
   * one deliberately does not close the cycle.
   */
  outcome?: OutcomeState;
};

export type ResolvedCanonicalExperienceContext = {
  context: ExperienceContext;
  capability: CanonicalExperienceCapability;
};

function asSexAtBirth(value: string | null | undefined): SexAtBirth {
  if (value === "AFAB" || value === "female") return "AFAB";
  if (value === "AMAB" || value === "male") return "AMAB";
  return "unknown";
}

function asViewerRole(
  value: string | null | undefined,
  isPrimary: boolean,
): ViewerRole {
  switch (value) {
    case "partner":
      return "partner";
    case "donor":
      return "donor";
    case "gestational-carrier":
      return "carrier";
    case "intended-parent":
      return "intended-parent";
    case null:
    case undefined:
      return isPrimary ? "primary" : "unknown";
    default:
      return "unknown";
  }
}

function asPrimaryIntent(value: string | null | undefined): PrimaryIntent {
  switch (value) {
    case "preserve":
    case "grow":
    case "donate":
    case "learn":
      return value;
    default:
      return "unknown";
  }
}

function asTreatmentGoal(value: string | null | undefined): TreatmentGoal {
  switch (value) {
    case "egg-freezing":
    case "sperm-freezing":
    case "embryo-freezing":
    case "pregnancy":
    case "ivf":
    case "iui":
    case "fet":
    case "egg-donation":
    case "sperm-donation":
    case "surrogacy":
    case "adoption":
    case "exploring":
      return value;
    case "egg-preserve":
      return "egg-freezing";
    case "sperm-preserve":
      return "sperm-freezing";
    case "embryo-preserve":
      return "embryo-freezing";
    default:
      return "unknown";
  }
}

function asJourneyStage(value: string | null | undefined): JourneyStage {
  switch (value) {
    case "explore":
    case "trying-naturally":
    case "before-treatment":
    case "in-care":
    case "time-to-wait":
    case "complete":
    case "screening":
    case "matched":
    case "stimulation":
    case "retrieval":
    case "approved":
    case "donating":
      return value;
    default:
      return "unknown";
  }
}

function normalizeJourneyStage(
  goal: TreatmentGoal,
  stage: JourneyStage,
  recordedRole: ViewerRole | null,
): JourneyStage {
  const activeRole = resolveActiveViewerRole(goal, recordedRole);
  const hasStaleHelperJourney =
    isHelperViewerRole(recordedRole) && activeRole === null;

  if (
    hasStaleHelperJourney &&
    (stage === "screening" ||
      stage === "matched" ||
      stage === "approved" ||
      stage === "donating")
  ) {
    return "before-treatment";
  }

  return stage;
}

function asPartnerChoice(value: string | null | undefined): PartnerChoice {
  switch (value) {
    case "solo":
    case "with-partner":
    case "using-donor":
    case "with-surrogate":
      return value;
    default:
      return "unknown";
  }
}

function asCycleType(value: string | null | undefined): CycleType {
  switch (value) {
    case "ivf":
    case "iui":
    case "fet":
    case "icsi":
    case "oi":
    case "egg_freezing":
    case "embryo_freezing":
    case "sperm_freezing":
      return value;
    default:
      return "unknown";
  }
}

function asCyclePhase(value: string | null | undefined): CyclePhase {
  switch (value) {
    case "pre_treatment":
    case "protocol":
    case "stimulation":
    case "trigger":
    case "trigger_shot":
    case "egg_retrieval":
    case "sperm_collection":
    case "fertilization":
    case "insemination":
    case "embryo_development":
    case "transfer":
    case "luteal_phase":
    case "freezing":
      return value;
    default:
      return "unknown";
  }
}

function asTreatmentModality(
  context: CanonicalExperienceContext,
): TreatmentModality {
  const plan = context.acceptedTreatmentPlan;
  if (!plan) {
    if (context.intent.stage === "trying-naturally") return "natural";

    for (const plannedModality of context.intent.plannedModalities) {
      if (
        plannedModality === "ivf" ||
        plannedModality === "iui" ||
        plannedModality === "fet"
      ) {
        return plannedModality;
      }
    }

    return "unknown";
  }

  if (
    plan.fertilizationMethod === "icsi" ||
    plan.fertilizationMethod === "split_ivf_icsi" ||
    plan.fertilizationMethod === "rescue_icsi"
  ) {
    return "icsi";
  }

  switch (plan.treatmentFamily) {
    case "ivf":
      return "ivf";
    case "iui":
      return "iui";
    case "fet":
      return "fet";
    case "oi":
      return "oi";
    default:
      return "unknown";
  }
}

function goalFromPlan(
  context: CanonicalExperienceContext,
): TreatmentGoal | null {
  switch (context.acceptedTreatmentPlan?.treatmentFamily) {
    case "egg_freezing":
      return "egg-freezing";
    case "sperm_freezing":
      return "sperm-freezing";
    case "embryo_freezing":
      return "embryo-freezing";
    case "ivf":
      return "ivf";
    case "iui":
      return "iui";
    case "fet":
      return "fet";
    default:
      return null;
  }
}

function mapSource(
  value: string | null | undefined,
  noneValue: "none" | "unknown" = "unknown",
): "self" | "partner" | "donor" | "unknown" | "none" {
  switch (value) {
    case "self":
      return "self";
    case "partner":
      return "partner";
    case "donor":
      return "donor";
    case "not_applicable":
      return noneValue === "none" ? "none" : "unknown";
    default:
      return "unknown";
  }
}

function buildInput(
  canonical: CanonicalExperienceContext,
  fallback: CanonicalExperienceContextFallback,
): ExperienceContextInput {
  const goal =
    asTreatmentGoal(canonical.intent.goal) === "unknown"
      ? (goalFromPlan(canonical) ?? "unknown")
      : asTreatmentGoal(canonical.intent.goal);
  const role = asViewerRole(
    canonical.viewer.participantRole,
    canonical.viewer.isPrimary,
  );
  const activeRole = resolveActiveViewerRole(goal, role);
  const viewerRole =
    activeRole ?? (canonical.viewer.isPrimary ? "primary" : "unknown");
  const journeyStage = normalizeJourneyStage(
    goal,
    asJourneyStage(canonical.intent.stage),
    role,
  );
  const cycleType = asCycleType(canonical.activeCycle?.cycleType);
  const canonicalSexAtBirth = asSexAtBirth(canonical.viewer.sexAtBirth);
  const canonicalPartnerChoice = asPartnerChoice(
    canonical.intent.partnerChoice,
  );
  const plan = canonical.acceptedTreatmentPlan;

  return {
    viewer: {
      role: viewerRole,
      sexAtBirth:
        canonicalSexAtBirth === "unknown"
          ? (fallback.sexAtBirth ?? "unknown")
          : canonicalSexAtBirth,
      isPrimary: canonical.viewer.isPrimary,
      partnerConnected: canonical.viewer.partnerConnected,
    },
    familyBuildingPlan: {
      primaryIntent:
        canonical.intent.primaryIntent === null
          ? undefined
          : goal === "egg-freezing" ||
              goal === "sperm-freezing" ||
              goal === "embryo-freezing"
            ? "preserve"
            : asPrimaryIntent(canonical.intent.primaryIntent),
      treatmentGoal: goal,
      partnerChoice:
        canonicalPartnerChoice === "unknown"
          ? (fallback.partnerChoice ?? "unknown")
          : canonicalPartnerChoice,
      modality: asTreatmentModality(canonical),
      // Sources are authoritative only when the accepted plan states them.
      // Passing explicit unknowns for an absent plan prevents the legacy
      // resolver from deriving material sources from sex or relationship.
      eggSource: mapSource(plan?.eggSource, "none"),
      spermSource: mapSource(plan?.spermSource, "none"),
      carrierSource: "unknown",
    },
    lifecycle: {
      journeyStage,
      outcome: fallback.outcome ?? "none",
    },
    treatment: {
      hasActiveCycle: canonical.activeCycle !== null,
      cycleType,
      currentPhase: asCyclePhase(canonical.activeCycle?.currentPhaseType),
      cycleStatus:
        canonical.activeCycle?.status === "open" ||
        canonical.activeCycle?.status === "closed"
          ? canonical.activeCycle.status
          : "unknown",
    },
    dataAvailability: {
      profile: "real",
    },
  };
}

/**
 * Adapt the server projection into moon's existing resolver vocabulary. The
 * adapter is deliberately pure: all source data comes from the API response,
 * and the existing resolver remains responsible for derived body, material,
 * process, and lifecycle contexts.
 */
export function resolveCanonicalExperienceContext(
  canonical: CanonicalExperienceContext,
  fallback: CanonicalExperienceContextFallback = {},
): ResolvedCanonicalExperienceContext {
  return {
    context: resolveExperienceContext(buildInput(canonical, fallback)),
    capability: canonical.capability,
  };
}
